import { useState } from "react";
import { Link } from "react-router";
import { Heart } from "lucide-react";
import Lottie from "lottie-react";
import animationData from "@/assets/enter-password.lottie.json";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * MinimalNav — the marketing-landing nav per
 * `.claude/skills/sketch-findings-teachable_clone/references/components.md`
 * § MinimalNav and `landing-philosophy.md` (billboard direction).
 *
 * Contract:
 * - Sticky frosted-glass header (matches the TopNav pattern).
 * - Left: 28px coral brand mark (heart-in-circle) + "Kidology" wordmark
 *   → links to `/` (the marketing landing itself).
 * - Right: muted note "Access through your Kidology app" (hidden below sm
 *   breakpoint) + coral "How to Login" pill button that opens a centered
 *   shadcn Dialog explaining the magic-link auth flow.
 * - The magic-link modal: small Lottie illustration on top (the
 *   `enter-password.lottie.json` phone+password animation), "How to Login"
 *   title, primary copy about magic links being emailed to paying users,
 *   visual gap, then a secondary line with a `mailto:support@kidology.app`
 *   escape hatch. Escape / X / overlay-click close come from Radix.
 * - ZERO internal navigation links. Per `project_all_behind_auth.md`, every
 *   authenticated surface (Courses / Library / Profile / Consultations)
 *   lives behind auth and is NOT linked from the marketing landing.
 *
 * Mounted at `/` by `Landing.tsx`.
 */
export type MinimalNavProps = Record<string, never>;

import { getTenantConfig } from "@/lib/tenant";

export function MinimalNav() {
  const tenant = getTenantConfig();
  // One-shot reduced-motion read
  const [reducedMotion] = useState<boolean>(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  return (
    <header className="sticky top-0 z-30 backdrop-blur bg-background/85 supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-5 lg:px-8">
        <Link
          to="/"
          className="flex items-center gap-2.5 text-xl font-extrabold tracking-tight text-foreground"
        >
          {tenant.brandName === "MindfulMe" ? (
            <span className="flex items-center gap-2 font-black text-slate-900 tracking-tight text-xl sm:text-2xl">
              <span className="relative flex size-7 items-center justify-center">
                <span className="absolute left-0 size-5 rounded-full bg-indigo-600" />
                <span className="absolute right-0 size-5 rounded-full bg-orange-500 opacity-90 mix-blend-multiply" />
              </span>
              <span>Mindful<span className="text-indigo-600">Me</span></span>
            </span>
          ) : (
            <>
              <span
                className="inline-flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground"
                aria-hidden="true"
              >
                <Heart className="size-3.5 fill-current" />
              </span>
              <span className="text-primary font-bold">{tenant.brandName}</span>
            </>
          )}
        </Link>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-muted-foreground sm:inline">
            Access through your {tenant.brandName} app
          </span>
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="minimal-nav-how-to-login-trigger"
              >
                How to Login
              </button>
            </DialogTrigger>
            <DialogContent>
              <div
                aria-hidden="true"
                className="mx-auto w-32 h-32 sm:w-40 sm:h-40"
              >
                <Lottie
                  animationData={animationData}
                  loop={!reducedMotion}
                  autoplay={!reducedMotion}
                  aria-hidden="true"
                />
              </div>
              <DialogHeader>
                <DialogTitle>How to Login</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <DialogDescription>
                  If you&apos;ve paid for {tenant.brandName}, a magic link was sent to you
                </DialogDescription>

                
                <div className="pt-1">
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Don&apos;t have a magic link?
                    <br />
                    Enter your email below and we send you a new link
                  </p>
                  
                  {status === "success" ? (
                    <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-500 border border-emerald-500/20">
                      If you purchased the courses, a login link has been sent to your email.
                    </div>
                  ) : (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!email.trim()) return;
                        setStatus("loading");
                        setErrorMessage("");
                        try {
                          await api("/api/auth/request-magic-link", {
                            method: "POST",
                            body: JSON.stringify({ email: email.trim() }),
                          });
                          setStatus("success");
                        } catch (err) {
                          setStatus("error");
                          const msg = apiErrorMessage(err, "Failed to send link.");
                          if (msg === "user_not_found") {
                            setErrorMessage("User with this email was not found.");
                          } else if (msg === "token_not_found") {
                            setErrorMessage("No login link has been issued for this email.");
                          } else {
                            setErrorMessage(msg);
                          }
                        }
                      }}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <label htmlFor="login-email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-3">
                          Email
                        </label>
                        <input
                          id="login-email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@example.com"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={status === "loading"}
                        />
                      </div>
                      
                      {status === "error" && (
                        <p className="text-sm text-destructive">{errorMessage}</p>
                      )}
                      
                      <button
                        type="submit"
                        disabled={status === "loading"}
                        className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        {status === "loading" ? "Sending..." : "Send magic link"}
                      </button>
                    </form>
                  )}
                </div>

                <p className="text-xs text-muted-foreground pt-2">
                  Other questions? Contact our support at{" "}
                  <a
                    href="mailto:support@kidology.app"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                  >
                    support@kidology.app
                  </a>
                </p>

              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </header>
  );
}
